import type { PlayerSessionView, RoomSessionResponse } from "@secret-cabinet/contracts";
import { useEffect, useState } from "react";
import { Landing } from "./Landing";
import { JoinGate } from "./JoinGate";
import { Room, StaticRoomPreview } from "./Room";
import { AppFailure } from "./components/AppErrorBoundary";
import { clearPlayerSession, loadPlayerSession, savePlayerSession } from "./session";
import { visualFixtureFor } from "./visual-fixtures";

interface RouteState {
  code: string | null;
  tv: boolean;
}

export function App() {
  const [route, setRoute] = useState<RouteState>(readRoute);
  const [session, setSession] = useState<PlayerSessionView | null>(() =>
    route.code && !route.tv ? loadPlayerSession(route.code) : null,
  );

  useEffect(() => {
    const handleHistoryChange = (): void => {
      const next = readRoute();
      setRoute(next);
      setSession(next.code && !next.tv ? loadPlayerSession(next.code) : null);
    };
    window.addEventListener("popstate", handleHistoryChange);
    return () => window.removeEventListener("popstate", handleHistoryChange);
  }, []);

  const fixtureParams = new URLSearchParams(window.location.search);
  if (
    import.meta.env.VITE_VISUAL_FIXTURES === "1" &&
    fixtureParams.get("fixture") === "app-failure"
  ) {
    return <AppFailure />;
  }
  const visualFixture =
    import.meta.env.VITE_VISUAL_FIXTURES === "1" ? visualFixtureFor(fixtureParams) : null;
  if (visualFixture) {
    return (
      <StaticRoomPreview
        view={visualFixture.view}
        mode={visualFixture.mode}
        initialSection={visualFixture.section ?? "chamber"}
        status={visualFixture.status ?? "live"}
        error={visualFixture.error ?? null}
        actionError={visualFixture.actionError ?? null}
        pending={visualFixture.pending ?? false}
        afkWarningMs={visualFixture.afkWarningMs ?? null}
        presenceRequired={visualFixture.presenceRequired ?? false}
      />
    );
  }

  function openRoom(response: RoomSessionResponse, tv: boolean): void {
    if (response.player) {
      savePlayerSession(response.code, response.player);
      setSession(response.player);
    } else {
      setSession(null);
    }
    navigate({ code: response.code, tv });
  }

  function navigate(next: RouteState): void {
    const url = new URL(window.location.href);
    if (next.code) {
      url.searchParams.set("room", next.code);
    } else {
      url.searchParams.delete("room");
    }
    if (next.tv) {
      url.searchParams.set("tv", "1");
    } else {
      url.searchParams.delete("tv");
    }
    window.history.pushState({}, "", url);
    setRoute(next);
  }

  function leaveRoom(): void {
    if (route.code && !route.tv) {
      clearPlayerSession(route.code);
    }
    setSession(null);
    navigate({ code: null, tv: false });
  }

  if (!route.code) {
    return (
      <Landing
        onRoomCreated={(response) => openRoom(response, true)}
        onJoinCode={(code) => {
          setSession(loadPlayerSession(code));
          navigate({ code, tv: false });
        }}
      />
    );
  }

  if (!route.tv && !session) {
    return (
      <JoinGate
        code={route.code}
        onBack={() => navigate({ code: null, tv: false })}
        onJoined={(response) => openRoom(response, false)}
      />
    );
  }

  return (
    <Room
      code={route.code}
      mode={route.tv ? "tv" : "player"}
      session={session}
      onExit={route.tv ? () => navigate({ code: null, tv: false }) : leaveRoom}
      onLeave={leaveRoom}
      onForgetSession={() => {
        clearPlayerSession(route.code!);
        setSession(null);
      }}
    />
  );
}

function readRoute(): RouteState {
  const params = new URLSearchParams(window.location.search);
  const rawCode = params.get("room")?.trim().toUpperCase() ?? "";
  return {
    code: /^[ABCDEFGHJKLMNPQRSTUVWXYZ]{4}$/.test(rawCode) ? rawCode : null,
    tv: params.get("tv") === "1",
  };
}
